import { observer } from 'owa-mobx-react';
import type { FavoriteNodeCommonProps } from './FavoriteNode';
import DragAndDroppable from 'owa-dnd/lib/components/DragAndDroppable';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import type { FavoriteNodeDragData } from 'owa-favorites-types';
import { type FolderForestNodeType } from 'owa-favorites-types';
import folderStore, { getEffectiveFolderDisplayName } from 'owa-folders';
import type { MailFolder } from 'owa-graph-schema';
import { isMoveCopyAllowed } from 'owa-mail-triage-movecopy-importexport/lib/utils/copyMoveItemUtils';
import { showFavoritesContextMenu } from 'owa-mail-favorites-store/lib/actions/favoritesContextMenu';
import { getCustomBundledIcon } from 'owa-folders-common/lib/util/getCustomIconComponent';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { getFolderViewStateFromIdNoFallback } from 'owa-mail-folder-store/lib/selectors/getFolderViewStateFromId';
import { getCustomFolderColorHexValue } from 'owa-mail-folder-store/lib/selectors/getCustomFolderColor';

import { DRAG_X_OFFSET, DRAG_Y_OFFSET, FolderNode } from 'owa-mail-folder-view';
import { getSelectedTableView } from 'owa-mail-list-store';
import type { MailListItemPartDragData } from 'owa-mail-types/lib/types/MailListItemPartDragData';
import type { MailListRowDragData } from 'owa-mail-types/lib/types/MailListRowDragData';
import { getAnchorForContextMenu } from 'owa-positioning';
import publicFolderFavoriteStore from 'owa-public-folder-favorite/lib/store/publicFolderFavoriteStore';
import React from 'react';
import { dragPreview } from './FavoriteNode.scss';
import getFavoriteFolderDisplayName from '../util/getFavoriteFolderDisplayName';
import {
    canDropFavorite,
    canDropFolder,
    onDropFolder,
    onDropFavorite,
    onDropMailListItemPart,
    onDropMailListRows,
} from '../util/dragDropUtils';
import type DropEffect from 'owa-dnd/lib/store/schema/DropEffect';
import { SEARCH_FOLDER_TYPE_NAME, isDragDropDisabledFolderType } from 'owa-folders-constants';
import {
    FolderSearchRegular,
    FolderSearchFilled,
    bundleIcon,
    FolderPersonRegular,
    FolderPersonFilled,
    CloudArchiveRegular,
    CloudArchiveFilled,
} from '@fluentui/react-icons';
import { isMailboxSharedOrDelegate } from 'owa-account-shared-mailbox-utils';
import FavoritePlaceholderNode from './FavoritePlaceholderNode';
import getCombinedFavoritesFromCache from 'owa-favorites/lib/utils/getCombinedFavoritesFromCache';
import { getFavoriteNodeViewStateFromId } from 'owa-mail-favorites-store';

const FolderSearchIcons = bundleIcon(FolderSearchFilled, FolderSearchRegular);
const FolderPersonIcons = bundleIcon(FolderPersonFilled, FolderPersonRegular);
const FolderArchiveIcons = bundleIcon(CloudArchiveFilled, CloudArchiveRegular);

export interface FavoriteFolderNodeProps extends FavoriteNodeCommonProps {
    folderId: string;
    isPublicFolder?: boolean;
    ellipsesOnHover?: boolean;
    customTreeNodeTooltip?: string;
}

const defaultExport: React.FunctionComponent<FavoriteFolderNodeProps> = observer(
    function FavoriteFolderNode(props: FavoriteFolderNodeProps) {
        const { isPublicFolder, folderId, favoriteId, customTreeNodeTooltip } = props;

        // Besides the isDragOver property in store, we also add this property here to distinguish from the dropping on MailFolderNode
        const isDragOver = React.useRef<boolean>();

        const getFolder = (): MailFolder | undefined => {
            return isPublicFolder
                ? publicFolderFavoriteStore().folderTable.get(folderId)
                : folderStore().folderTable.get(folderId);
        };

        const folder = getFolder();

        const customIconComponent = props.hideIcon
            ? undefined
            : getFavoriteFolderIcon(folder, folderId);

        /**
         * A callback function to check if current drag data is able to drop on current element
         * @param dragData the drag data which will be dropped
         * @param dragEvent the drag event to get the ctrlKey property value
         *
         * @returns the desired drop effect that will be used to update the cursor style
         */
        const canDrop = React.useCallback(
            (dragData: DragData, dragEvent?: React.DragEvent<HTMLElement>): DropEffect => {
                if (!folder) {
                    return 'none';
                }
                const itemType = dragData.itemType;

                if (isDragDropDisabledFolderType(folder.distinguishedFolderType)) {
                    return 'none';
                }

                switch (itemType) {
                    // Conversations can't be moved to PF
                    case DraggableItemTypes.MailListItemPart:
                    case DraggableItemTypes.MailListRow:
                    case DraggableItemTypes.MultiMailListMessageRows:
                    case DraggableItemTypes.MultiMailListConversationRows: {
                        const tableView = getSelectedTableView();
                        const draggedItemsMailboxInfo = getMailboxInfo(tableView);
                        if (isMoveCopyAllowed(draggedItemsMailboxInfo, folder.mailboxInfo)) {
                            return dragEvent?.ctrlKey ? 'copy' : 'move';
                        }
                        return 'none';
                    }

                    case DraggableItemTypes.FavoriteNode: {
                        return canDropFavorite(dragData, folder.mailboxInfo) ? 'move' : 'none';
                    }
                    case DraggableItemTypes.MailFolderNode: {
                        return canDropFolder(dragData, folder.mailboxInfo) ? 'move' : 'none';
                    }
                    default:
                        return 'none';
                }
            },
            [folder]
        );

        const onDrop = React.useCallback(
            async (
                dragData: DragData,
                _pageX: number,
                _pageY: number,
                _currentTarget?: HTMLElement,
                ctrlKey?: boolean
            ) => {
                const itemType = dragData.itemType;

                switch (itemType) {
                    case DraggableItemTypes.MailListItemPart:
                        await onDropMailListItemPart(
                            dragData as MailListItemPartDragData,
                            folderId,
                            props.actionSource
                        );

                        break;
                    case DraggableItemTypes.MailListRow:
                    case DraggableItemTypes.MultiMailListConversationRows:
                    case DraggableItemTypes.MultiMailListMessageRows:
                        await onDropMailListRows(
                            dragData as MailListRowDragData,
                            folderId,
                            ctrlKey,
                            props.actionSource
                        );
                        break;
                    case DraggableItemTypes.MailFolderNode:
                        {
                            if (!folder) {
                                break;
                            }
                            await onDropFolder(
                                dragData,
                                folder.mailboxInfo,
                                favoriteId,
                                props.actionSource
                            );
                        }
                        break;
                    case DraggableItemTypes.FavoriteNode:
                        {
                            await onDropFavorite(dragData, favoriteId, props.actionSource);
                        }
                        break;
                }
            },
            [folder, favoriteId, folderId, props.actionSource]
        );

        const onContextMenu = React.useCallback(
            (evt: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
                evt.stopPropagation();
                evt.preventDefault();
                showFavoritesContextMenu(
                    favoriteId,
                    isPublicFolder ? 5 : 0,
                    getAnchorForContextMenu(evt),
                    folderId,
                    undefined /* showRootMenu */,
                    undefined /* isPlaceholderNode */,
                    props.actionSource
                );
            },
            [favoriteId, isPublicFolder, folderId, props.actionSource]
        );

        const onDragOver = React.useCallback(() => {
            isDragOver.current = true;
        }, []);

        const onDragLeave = React.useCallback(() => {
            isDragOver.current = false;
        }, []);

        const getDragData = React.useCallback(() => {
            const displayName = getEffectiveFolderDisplayName(folder);
            const folderNodeDragData: FavoriteNodeDragData = {
                itemType: DraggableItemTypes.FavoriteNode,
                favoriteId,
                favoriteType: isPublicFolder ? 5 : 0,
                displayName,
                itemData: {
                    mailboxInfo: folder?.mailboxInfo,
                },
            };
            return folderNodeDragData;
        }, [folder, favoriteId, isPublicFolder]);

        const folderViewState = getFavoriteNodeViewStateFromId(favoriteId);

        const itemType = folderViewState?.drop?.draggableItemType;
        const ellipsesOnHover = props.ellipsesOnHover;

        const folderDisplayName = getFavoriteFolderDisplayName(folderId, false);
        const folderNameAndTooltip = getFavoriteFolderDisplayName(folderId, true);

        if (!folder || !folderViewState || !folderViewState?.drag || !folderViewState?.drop) {
            const cachedFavorite = getCombinedFavoritesFromCache()?.combinedFavorites?.find(
                favorite => favorite?.favoriteId == props.favoriteId
            );
            if (cachedFavorite) {
                return (
                    <FavoritePlaceholderNode
                        favoriteId={cachedFavorite.favoriteId}
                        id={cachedFavorite.uniqueId}
                        type={cachedFavorite.type}
                        mailboxInfo={cachedFavorite.mailboxInfo}
                        displayName={cachedFavorite.displayName || folderDisplayName || ''}
                        isVisible={cachedFavorite.isVisible}
                        actionSource={props.actionSource}
                    />
                );
            }
            return null;
        }

        const folderColorId = getFolderViewStateFromIdNoFallback(folderId)?.color;
        const customFolderColor = getCustomFolderColorHexValue(folderColorId);

        return (
            <DragAndDroppable
                dragViewState={folderViewState.drag}
                getDragData={getDragData}
                getDragPreview={getDragPreview}
                xOffset={DRAG_X_OFFSET}
                yOffset={DRAG_Y_OFFSET}
                dropViewState={folderViewState.drop}
                onDrop={onDrop}
                canDrop={canDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
            >
                <FolderNode
                    customIconComponent={customIconComponent}
                    displayName={folderDisplayName}
                    folderId={folderId}
                    isDroppedOver={folderViewState.drop.isDragOver && isDragOver.current}
                    isBeingDragged={folderViewState.drag.isBeingDragged}
                    key={folderId}
                    onContextMenu={onContextMenu}
                    showHoverStateOnDroppedOver={
                        itemType === DraggableItemTypes.MailListRow ||
                        itemType === DraggableItemTypes.MultiMailListConversationRows ||
                        itemType === DraggableItemTypes.MultiMailListMessageRows ||
                        itemType === DraggableItemTypes.MailListItemPart
                    }
                    treeType={'favorites'}
                    isPublicFolder={isPublicFolder}
                    unreadCount={folder?.UnreadCount ?? 0}
                    totalCount={folder?.totalMessageCount ?? 0}
                    distinguishedFolderId={folder?.distinguishedFolderType ?? ''}
                    ellipsesOnHover={ellipsesOnHover}
                    isVerticalNode={props.isVerticalNode}
                    hideIcon={props.hideIcon}
                    customIconColor={customFolderColor}
                    tooltipNameContent={folderNameAndTooltip}
                    customTreeNodeTooltip={customTreeNodeTooltip}
                />
            </DragAndDroppable>
        );
    },
    'FavoriteFolderNode'
);
export default defaultExport;

function getDragPreview(dragData: DragData) {
    const elem = document.createElement('div');
    elem.className = dragPreview;
    elem.innerText = (dragData as FavoriteNodeDragData).displayName;
    return elem;
}

function getFavoriteFolderIcon(folder: MailFolder | undefined, folderId: string) {
    if (folder?.type === SEARCH_FOLDER_TYPE_NAME) {
        return FolderSearchIcons;
    } else if (folder?.mailboxInfo && isMailboxSharedOrDelegate(folder?.mailboxInfo)) {
        return FolderPersonIcons;
    } else if (folder?.mailboxInfo.type === 'ArchiveMailbox') {
        return FolderArchiveIcons;
    } else {
        return getCustomBundledIcon(folderId, true);
    }
}
